import { useEffect, useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';

function pickScheduleRows(scheduleMap, hints) {
  const map = scheduleMap && typeof scheduleMap === 'object' ? scheduleMap : {};
  const keys = Object.keys(map);
  const uniqueHints = [...new Set(hints.map((h) => String(h || '').trim()).filter(Boolean))];
  for (const h of uniqueHints) {
    const rows = map[h];
    if (Array.isArray(rows) && rows.length) return rows;
  }
  for (const h of uniqueHints) {
    const found = keys.find((k) => k.toLowerCase() === h.toLowerCase());
    if (found) {
      const rows = map[found];
      if (Array.isArray(rows) && rows.length) return rows;
    }
  }
  return [];
}

export default function ScheduleTable({ classCode, school, name, selectedDay, numClassesPerDay, language }) {
  const [data, setData] = useState([]);
  const [classes, setClasses] = useState(numClassesPerDay);
  
  const dayToShow = selectedDay;

  const getWeekNumberFromYearStart = (date) => {
    const startOfYear = new Date(date.getFullYear(), 0, 1);
    const diffInMs = date - startOfYear;
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
    return Math.floor(diffInDays / 7) + 1;
  };

  useEffect(() => {
    const fetchSchedule = async () => {
      if (!classCode || !school || !name) return;
      const snapshot = await getDoc(doc(db, school, classCode));
      if (!snapshot.exists()) return;

      const scheduleMap = JSON.parse(snapshot.data().schedule || '{}');
      const hints = [name];
      const user = auth.currentUser;
      if (user?.email) {
        const uSnap = await getDoc(doc(db, 'peerTutoring', 'users', 'userDocs', user.email.toLowerCase()));
        if (uSnap.exists()) {
          const u = uSnap.data();
          const full = [u.firstName, u.lastName].filter(Boolean).join(' ').trim();
          if (full) hints.push(full);
          if (u.firstName) hints.push(String(u.firstName).trim());
          if (u.lastName) hints.push(String(u.lastName).trim());
          if (u.name) hints.push(String(u.name).trim());
        }
      }
      const parsed = pickScheduleRows(scheduleMap, hints);

      setData(parsed);
      setClasses(+snapshot.data().classes);
    };
    fetchSchedule();
  }, [classCode, school, name]);
  
  const subjectsForDay = data.slice(
    dayToShow * classes,
    (dayToShow + 1) * classes
  );

  const currentWeekNumber = getWeekNumberFromYearStart(new Date());
  const isOddWeek = currentWeekNumber % 2 === 1;

  const displayedSubjectsForDay = subjectsForDay.map((subject) => {
    if (!subject || typeof subject !== 'string') return subject;

    // Support both "/" and "\" as separators between two alternating subjects
    const parts = subject.split(/[\\/]/).filter(Boolean);
    if (parts.length < 2) return subject;

    const first = parts[0].trim();
    const second = parts[1].trim();
    if (!first || !second) return subject;

    return isOddWeek ? first : second;
  });

  return (
    <TableContainer
      component={Paper}
      elevation={4}
      sx={{
        mt: 3,
        borderRadius: 3,
        maxHeight: {
          xs: `${8 + classes * 7.2}vh`,  
          md: 'none',                    
        },

        overflowY: 'auto',
        overflowX: 'auto',
        width: 'max-content',
        mx: 'auto',
      }}
    >
      <Table size="medium" stickyHeader>
        <TableHead>
          <TableRow sx={{ backgroundColor: '#e0f7f7' }}>
            <TableCell sx={{ fontWeight: 700 }}>{language ? 'Period' : 'Դասաժամ'}</TableCell>
            <TableCell sx={{ fontWeight: 700, textAlign: 'center' }}>{language ? 'Subject' : 'Առարկա'}</TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {displayedSubjectsForDay.map((subject, index) => (
            <TableRow
              key={index}
              sx={{
                backgroundColor: index % 2 === 0 ? 'action.hover' : 'inherit',
                '&:hover': { backgroundColor: 'action.selected' },
              }}
            >
              <TableCell sx={{ fontWeight: 600 }} align='center'>
                {index + 1}
              </TableCell>
              <TableCell align='center'>{subject || '—'}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
